import React, { useState, useEffect, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import Toolbar from './Toolbar'
import { SecondaryButton } from 'components/Shared/Button'
import EventsList from './EventsList'
import { getPaginatedSimilarEvents } from '../constants'
import moment from 'moment'
import { useEditEventSeriesContext } from '../EditEventSeriesProvider'
import { useMediaQuery } from 'hooks/useMediaQuery'

export default function FilterTab() {
  const { t } = useTranslation()

  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [filteredEvents, setFilteredEvents] = useState([])
  const [isShown, setIsShown] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  // Store the dates that were used for filtering (not the current date picker values)
  const [filterStartDate, setFilterStartDate] = useState(null)
  const [filterEndDate, setFilterEndDate] = useState(null)

  const { similarEvents, clearSelection, includedEvents } = useEditEventSeriesContext()

  const isDesktop = useMediaQuery('(min-width: 1149px)')

  // Apply filter function using saved filter dates
  const applyFilter = useCallback(() => {
    if (!filterStartDate || !filterEndDate) return
    
    const formattedStartDate = moment(filterStartDate).format('YYYY-MM-DD')
    const formattedEndDate = moment(filterEndDate).format('YYYY-MM-DD')
    
    const result = getPaginatedSimilarEvents(similarEvents, 1, formattedStartDate, formattedEndDate)
    setFilteredEvents(result.data)
  }, [similarEvents, filterStartDate, filterEndDate])

  const handleShowDate = () => {
    // Validate that both dates are selected
    if (!startDate || !endDate) {
      return
    }
    
    // Save current date picker values as filter dates
    setFilterStartDate(startDate)
    setFilterEndDate(endDate)
    setIsShown(true)
    
    // Apply filter immediately with current dates
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD')
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD')
    const result = getPaginatedSimilarEvents(similarEvents, 1, formattedStartDate, formattedEndDate)
    setFilteredEvents(result.data)
  }

  // Update filtered events when similarEvents changes (if filter is already applied)
  useEffect(() => {
    if (isShown && filterStartDate && filterEndDate) {
      applyFilter()
    }
  }, [isShown, filterStartDate, filterEndDate, applyFilter])

  useEffect(() => {
    return () => {
      clearSelection()
    }
  }, [clearSelection])

  return (
    <div>
      <p className='filter-tab_description'>{t('Event.editEventSeriesModal.filterDescription')}</p>

      <div className='filter-tab_wrapper'>
        <Toolbar events={filteredEvents} className='toolbar_filter-tab' isDisabled={!isShown || filteredEvents.length === 0}/>

        <div className='filter-tab_date-picker'>
          <div className='filter-tab_date-picker_wrapper'>
            <p className='filter-tab_date-picker_label'>{t('Event.editEventSeriesModal.fromDate')}</p>

            <div className="form-group input-cell input-cell--input input-cell--date separatePlaceholderBox ">
              <DatePicker 
                selected={startDate} 
                onChange={(date) => setStartDate(date)} 
                dateFormat="dd.MM.yyyy"
                placeholderText={t('Event.editEventSeriesModal.eventDate')}
                popperPlacement={includedEvents.length < 3 && isDesktop ? 'bottom-start' : 'top-start'}
                popperModifiers={[
                  {
                    name: 'flip',
                    options: {
                      fallbackPlacements: [],
                    },
                  },
                ]}
                onCalendarOpen={() => setIsCalendarOpen(true)}
                onCalendarClose={() => setIsCalendarOpen(false)}
                maxDate={endDate}
              />
            </div>
          </div>

          <div className='filter-tab_date-picker_wrapper'>
            <p className='filter-tab_date-picker_label'>{t('Event.editEventSeriesModal.untilDate')}</p>

            <div className="form-group input-cell input-cell--input input-cell--date separatePlaceholderBox ">
              <DatePicker
                selected={endDate} 
                onChange={(date) => setEndDate(date)} 
                dateFormat="dd.MM.yyyy"
                placeholderText={t('Event.editEventSeriesModal.eventDate')}
                popperPlacement={includedEvents.length < 3 && isDesktop ? 'bottom-start' : 'top-start'}
                popperModifiers={[
                  {
                    name: 'flip',
                    options: {
                      fallbackPlacements: [],
                    },
                  },
                ]}
                onCalendarOpen={() => setIsCalendarOpen(true)}
                onCalendarClose={() => setIsCalendarOpen(false)}
                minDate={startDate}
              />
            </div>
          </div>

          <SecondaryButton
            text={t('Event.editEventSeriesModal.showDate')}
            onClick={handleShowDate}
            classNames='filter-tab_date-picker_button'
            fullWidth={false}
            disabled={!startDate || !endDate}
          />
        </div>
      </div>

      <EventsList
        events={filteredEvents}
        isIncluded={false}
        emptyMessage={isShown ? t('Event.editEventSeriesModal.noSimilarEvents') : ''}
      />

      {isCalendarOpen && filteredEvents.length < 4 && includedEvents.length < 3 && isDesktop && (
        <div className='filter-tab_bottom-spacer' />
      )}
    </div>
  )
}

FilterTab.propTypes = {}